import { useState } from 'react'
import { ApiError } from '../api/client'
import { useOrganizationSettings, useSetTimeZone, useTimeZones } from '../api/hooks'
import { useAuth } from '../auth/AuthContext'

/**
 * The zone every schedule's cron expression is read in.
 *
 * One setting for the whole organization, which is why it lives on the schedule screen
 * rather than behind a settings menu: it is the only thing on the page that says what the
 * numbers in the Cron column mean. Changing it moves the hour every unattended close runs
 * at without touching a single schedule, so the confirmation step is deliberate.
 */
export function TimeZoneSetting() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')
  const settings = useOrganizationSettings()
  const [editing, setEditing] = useState(false)
  const [chosen, setChosen] = useState('')

  // Only fetched once the picker is opened. It is a few hundred entries and nobody who
  // is not changing the setting needs it.
  const zones = useTimeZones(editing)
  const save = useSetTimeZone()

  if (settings.isLoading) {
    return null
  }

  const current = settings.data

  if (!current) {
    return null
  }

  function begin() {
    setChosen(current!.timeZoneId)
    save.reset()
    setEditing(true)
  }

  function commit() {
    save.mutate(chosen, { onSuccess: () => setEditing(false) })
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-[12px]"
      style={{ borderColor: 'var(--color-rule-soft)' }}
    >
      <span className="col-head">time zone</span>

      {editing ? (
        <>
          <select
            className="input w-80"
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
            disabled={zones.isLoading || save.isPending}
          >
            {/*
                While the list loads, the current value is still shown as the only option
                rather than an empty box -- an empty select reads as "nothing is set",
                which is never true here.
            */}
            {zones.data === undefined ? (
              <option value={current.timeZoneId}>{current.timeZoneId}</option>
            ) : (
              zones.data.map((z) => (
                <option key={z.id} value={z.id}>
                  ({z.currentOffset}) {z.id}
                </option>
              ))
            )}
          </select>

          <button className="btn btn-sm" onClick={commit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            className="text-[12px] underline"
            style={{ color: 'var(--color-muted)' }}
            onClick={() => setEditing(false)}
            disabled={save.isPending}
          >
            Cancel
          </button>

          <span style={{ color: 'var(--color-faint)' }}>
            Every schedule moves to this zone. Nothing reruns; the next fire time changes.
          </span>
        </>
      ) : (
        <>
          <span className="ident">{current.timeZoneId}</span>
          <span style={{ color: 'var(--color-faint)' }}>
            ({current.currentOffset} now
            {current.displayName !== current.timeZoneId && ` — ${current.displayName}`})
          </span>

          {isAdmin && (
            <button
              className="text-[12px] underline"
              style={{ color: 'var(--color-accent)' }}
              onClick={begin}
            >
              Change
            </button>
          )}
        </>
      )}

      {/*
          A zone the server cannot resolve does not stop anything -- schedules keep
          running, as UTC -- which is exactly why it has to be said out loud. Nothing else
          in the product would ever mention it.
      */}
      {!current.isResolvable && (
        <span
          className="px-1.5 py-0.5"
          style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
          role="alert"
        >
          This server does not recognise “{current.timeZoneId}”, so schedules are being
          read as UTC.
        </span>
      )}

      {save.isError && (
        <span style={{ color: 'var(--color-error)' }} role="alert">
          {save.error instanceof ApiError ? save.error.message : 'Could not save the time zone.'}
        </span>
      )}
    </div>
  )
}
